import { useCallback, useEffect, useState } from 'react';
import useClient from 'api/client';
import { useAlert } from 'components/message/AlertContext';
import { useParams } from 'react-router-dom';
import { FormatDate } from 'components/dates/FormatDate';
import getEnvironment from 'api/Environment/getEnvironment';
import { Environment } from 'types/Environment';

const intervalTime = 20000;
export default function useEnvironment() {
    const client = useClient();
    const { openAlert } = useAlert();
    const params = useParams<{ uri: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [environment, setEnvironment] = useState<Environment>({
        environmentUri: '',
        created: '',
        label: '',
        userRoleInEnvironment: '',
        originSamlGroupName: [],
        originSamlFullDevGroupName: [],
        originSamlBasicDevGroupName: [],
        originSamlReaderGroupName: [],
        stack: {
            status: '',
        },
    });
    const [userIsAdmin, setUserIsAdmin] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (params.uri) {
                    setLoading(true);
                    const response = await client?.query(
                        getEnvironment(params.uri),
                    );
                    if (!response.errors) {
                        setEnvironment({
                            ...response.data.getEnvironment,
                            created: FormatDate(
                                response.data.getEnvironment.created,
                            ),
                        });
                        setUserIsAdmin(
                            ['Creator', 'Admin', 'Owner'].includes(
                                response.data.getEnvironment
                                    .userRoleInEnvironment,
                            ),
                        );
                    } else {
                        setError(true);
                        openAlert({
                            message:
                                'Something went wrong... Could not retrieve dataset',
                            severity: 'alert',
                        });
                    }
                }
            } finally {
                setLoading(false);
            }
        };

        if (client) {
            fetchData();
        }
    }, [client, params.uri]);

    const verifyEnvironmentChanged = useCallback(async () => {
        if (params.uri) {
            const response = await client.query(getEnvironment(params.uri));
            const data = response.data.getEnvironment;
            const newEnvironment = {
                ...data,
                created: FormatDate(data.created),
            };

            // Only set Environment if it's different than the one displayed
            if (
                JSON.stringify(environment) !== JSON.stringify(newEnvironment)
            ) {
                setEnvironment({
                    ...data,
                    created: FormatDate(data.created),
                });
            }
        }
    }, [client, params.uri, environment]);

    useEffect(() => {
        const interval = setInterval(async () => {
            if (client) {
                // polling for new Environment changes every 20 seconds
                await verifyEnvironmentChanged();
            }
        }, intervalTime);

        return () => clearInterval(interval);
    }, [client, verifyEnvironmentChanged]);

    return {
        environment,
        userIsAdmin,
        error,
        loading,
    };
}
